import { useCallback, useEffect, useState } from 'react';

import { requestJson } from '../../components/workflowShared';

export const MIN_PHASE_RUNS = 1;
export const MAX_PHASE_RUNS = 10;

// More workflow types can be added here; security audits are the only one so far.
export type AppWorkflowTypeId = 'security-audit';
export type AppWorkflowPhaseId = 'recon' | 'exploit' | 'validation';
export type AppWorkflowExecution = 'parallel' | 'sequential';

export interface AppWorkflowPhase {
  id: AppWorkflowPhaseId;
  /** Model card ids from the tool catalog. */
  models: string[];
  /** Skill and tool card ids from the tool catalog. */
  tools: string[];
  runs: number;
  execution: AppWorkflowExecution;
}

export interface AppWorkflow {
  id: string;
  type: AppWorkflowTypeId;
  name: string;
  description: string;
  phases: AppWorkflowPhase[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppWorkflowPhaseInfo {
  title: string;
  description: string;
}

export interface AppWorkflowType {
  id: AppWorkflowTypeId;
  title: string;
  description: string;
  phases: AppWorkflowPhase[];
}

export interface AppWorkflowStore {
  workflows: AppWorkflow[];
  isLoading: boolean;
  error: string;
  saveWorkflow: (workflow: AppWorkflow) => Promise<AppWorkflow>;
  duplicateWorkflow: (workflow: AppWorkflow) => Promise<void>;
  activateWorkflow: (id: string) => Promise<void>;
  deleteWorkflow: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const WORKFLOW_PHASES: Record<AppWorkflowPhaseId, AppWorkflowPhaseInfo> = {
  recon: {
    title: 'Recon',
    description: 'Map the attack surface: entry points, trust boundaries, privileged roles and invariants.'
  },
  exploit: {
    title: 'Exploit development',
    description: 'Turn the attack surface into concrete exploit paths and proof-of-concept tests.'
  },
  validation: {
    title: 'Validation',
    description: 'Reproduce each finding, drop false positives and calibrate severity before reporting.'
  }
};

export const WORKFLOW_TYPES: AppWorkflowType[] = [
  {
    id: 'security-audit',
    title: 'Security audit',
    description: 'Recon, exploit development and validation, each with its own models, tools and runs.',
    phases: [
      {
        id: 'recon',
        models: ['gpt-5-6'],
        tools: ['trail-of-bits-static-analysis', 'semgrep-code-security'],
        runs: 2,
        execution: 'parallel'
      },
      {
        id: 'exploit',
        models: ['daybreak-blue', 'claude-opus-5-5'],
        tools: ['sc-auditor', 'pashov'],
        runs: 3,
        execution: 'parallel'
      },
      {
        id: 'validation',
        models: ['gpt-5-6'],
        tools: ['forefy'],
        runs: 1,
        execution: 'sequential'
      }
    ]
  }
];

export const getWorkflowType = (id: AppWorkflowTypeId): AppWorkflowType => {
  return WORKFLOW_TYPES.find((type) => type.id === id) ?? WORKFLOW_TYPES[0];
};

const createWorkflowId = (): string => typeof window.crypto?.randomUUID === 'function'
  ? window.crypto.randomUUID()
  : `workflow-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** A new, unsaved workflow with the type's default phases. */
export const createWorkflow = (typeId: AppWorkflowTypeId): AppWorkflow => {
  const type = getWorkflowType(typeId);

  return {
    id: createWorkflowId(),
    type: type.id,
    name: '',
    description: '',
    phases: type.phases.map((phase) => ({ ...phase, models: [...phase.models], tools: [...phase.tools] })),
    isActive: true,
    createdAt: '',
    updatedAt: new Date().toISOString()
  };
};

export const getWorkflowRunCount = (workflow: AppWorkflow): number => {
  return workflow.phases.reduce((total, phase) => total + phase.runs, 0);
};

/** Each run uses one model, so a phase needs at least one model and no more models than runs. */
export const getPhaseProblem = (phase: AppWorkflowPhase): string | null => {
  if (phase.models.length === 0) {
    return 'needs a model';
  }

  return phase.models.length > phase.runs ? 'has more models than runs' : null;
};

/** Phases that cannot run as configured. */
export const getInvalidPhaseIds = (workflow: AppWorkflow): AppWorkflowPhaseId[] => {
  return workflow.phases.filter((phase) => getPhaseProblem(phase)).map((phase) => phase.id);
};

const toWorkflowPayload = (workflow: AppWorkflow) => ({
  type: workflow.type,
  name: workflow.name.trim(),
  description: workflow.description.trim(),
  phases: workflow.phases,
  // Saving a workflow makes it the workflow used by subsequent pentests.
  isActive: true
});

export function useWorkflows(isEnabled: boolean): AppWorkflowStore {
  const [workflows, setWorkflows] = useState<AppWorkflow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!isEnabled) {
      setWorkflows([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await requestJson<{ workflows: AppWorkflow[] }>('/workflows');

      setWorkflows(response.workflows);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load workflows.');
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Inserts or replaces the workflow and makes it the active pentest workflow. */
  const saveWorkflow = useCallback(async (workflow: AppWorkflow): Promise<AppWorkflow> => {
    const exists = workflows.some((entry) => entry.id === workflow.id);
    const response = await requestJson<{ workflow: AppWorkflow }>(
      exists ? `/workflows/${encodeURIComponent(workflow.id)}` : '/workflows',
      { method: exists ? 'PATCH' : 'POST', body: JSON.stringify(toWorkflowPayload(workflow)) }
    );

    setWorkflows((current) => [
      response.workflow,
      ...current
        .filter((entry) => entry.id !== response.workflow.id)
        .map((entry) => ({ ...entry, isActive: false }))
    ]);
    return response.workflow;
  }, [workflows]);

  const duplicateWorkflow = useCallback(async (workflow: AppWorkflow): Promise<void> => {
    setError('');
    try {
      await saveWorkflow({
        ...workflow,
        id: createWorkflowId(),
        name: `${workflow.name} copy`,
        isActive: true,
        createdAt: '',
        updatedAt: new Date().toISOString()
      });
    } catch (duplicateError) {
      setError(duplicateError instanceof Error ? duplicateError.message : 'Unable to duplicate workflow.');
    }
  }, [saveWorkflow]);

  /** Makes an existing workflow the one used by subsequent pentests, without touching its contents. */
  const activateWorkflow = useCallback(async (id: string): Promise<void> => {
    setError('');
    try {
      const response = await requestJson<{ workflow: AppWorkflow }>(
        `/workflows/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify({ isActive: true }) }
      );

      setWorkflows((current) => current.map((entry) => entry.id === id
        ? response.workflow
        : { ...entry, isActive: false }));
    } catch (activateError) {
      setError(activateError instanceof Error ? activateError.message : 'Unable to activate workflow.');
    }
  }, []);

  const deleteWorkflow = useCallback(async (id: string): Promise<void> => {
    setError('');
    try {
      await requestJson<undefined>(`/workflows/${encodeURIComponent(id)}`, { method: 'DELETE' });
      setWorkflows((current) => current.filter((entry) => entry.id !== id));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete workflow.');
    }
  }, []);

  return { workflows, isLoading, error, saveWorkflow, duplicateWorkflow, activateWorkflow, deleteWorkflow, refresh };
}
